import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import toast from "react-hot-toast";
import { getStore, setStore } from "utils/store";

interface UnisatWalletProps {
  address: string
  isConnected: boolean

  connect: () => void
  disconnected: () => void

  getAccounts: () => Promise<string>
  getBalance: () => Promise<any>

  getProvider: () => any
  getPublicKey: () => Promise<string>
}

const UnisatWalletContext = createContext<UnisatWalletProps | null>(null);

export const UnisatWalletProvider = ({ children }: { children: ReactNode }) => {
  const [address, setAddress] = useState('')
  const [isConnected, setIsConnected] = useState(false)

  const getProvider = useCallback(() => {
      if(typeof (window as any).unisat !== 'undefined') {
          return (window as any).unisat
      } else {
          return undefined
      }
  }, [])

  const getAccounts = useCallback(async() => {
      const providers = getProvider()
      if(!providers) return
      const result = await providers.getAccounts()
      if(result && result.length) {
          return result[0]
      } else {
          return ''
      }
  }, [getProvider])

  const getPublicKey = useCallback(async () => {
      const providers = getProvider()
      if(!providers) return
      const publicKey = await providers.getPublicKey()
      return publicKey
  }, [getProvider])

  const getBalance = useCallback(async () => {
      const providers = getProvider()
      if(!providers) return
      const result = await providers.getBalance()
      return result
  }, [getProvider])

  const walletConnected = useCallback((address: string) => {
      setAddress(address)
      setIsConnected(true)
      setStore('chainge.curWallet', 'unisat')
  }, [])

  const disconnected = useCallback(() => {
      setAddress('')
      setIsConnected(false)
      setStore('chainge.curWallet', '')
  }, [])

  useEffect(() => {
      const providers = getProvider()
      if(!providers) return
      const fnAccountsChanged = (accounts: Array<string>) => {
          if(accounts && accounts.length) {
              walletConnected(accounts[0])
          } else {
              disconnected()
          }
      }
      providers.on('accountsChanged', fnAccountsChanged);

      return () => {
          providers.removeListener('accountsChanged', fnAccountsChanged)
      }
  }, [disconnected, getProvider, walletConnected])


  const connect = useCallback(async () => {
      const provider = getProvider()
      if(!provider) {
          disconnected()
          toast( `Error: not install Unisat`,
              {
                duration: 3000,
                position: 'top-right',
                icon: '⚠️'
              }
          );
          return
      }
      const accounts = await provider.requestAccounts()
      if(accounts && accounts.length) {
          walletConnected(accounts[0])
      } else {
          disconnected()
      }

  }, [disconnected, getProvider, walletConnected])

  useEffect(() => {
      const fnTemp = async () => {
         const result = await getAccounts();
         if(result) {
          walletConnected(result)
         } else {
          disconnected()
         }
      }
      const walletName = getStore('chainge.curWallet')
      if(walletName === 'unisat') {
          fnTemp()
      }
  }, [disconnected, getAccounts, walletConnected])

  return (
    <UnisatWalletContext.Provider
      value={{
        address,
        isConnected,
        connect,
        disconnected,

        getAccounts,
        getBalance,

        getProvider,
        getPublicKey
      }}
    >
      {children}
    </UnisatWalletContext.Provider>
  );
};
export const useUnisatWallet = () => {
  return (
    useContext(UnisatWalletContext) || {
      address: '',
      isConnected: false,
      connect: () => {},
      disconnected: () => {},

      getAccounts: () => {},
      getBalance: () => {},

      getProvider: () => {},
      getPublicKey: () => {},
    } as UnisatWalletProps
  );
};
